import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboard.service';
import { DashboardStats, RecentExecution } from '../types/api.types';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const canCreateWorkflow = (() => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray((user as any).permissions) && (user as any).permissions.includes('workflow.create');
  })();

  const canViewWorkflows = (() => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray((user as any).permissions) && (user as any).permissions.includes('workflow.view');
  })();

  const canViewExecutions = (() => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray((user as any).permissions) && (user as any).permissions.includes('execution.view');
  })();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentExecutions, setRecentExecutions] = useState<RecentExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const dashboardData = await dashboardService.getDashboardStats();
      setStats(dashboardData);
      setRecentExecutions(dashboardData.recentExecutions);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <CircularProgress size={20} />;
      default:
        return <ScheduleIcon color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {user?.first_name || user?.email}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your workflows today.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Workflows
                  </Typography>
                  <Typography variant="h4">
                    {stats?.totalWorkflows || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PlayIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Executions
                  </Typography>
                  <Typography variant="h4">
                    {stats?.activeExecutions || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <ScheduleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Success Rate
                  </Typography>
                  <Typography variant="h4">
                    {stats?.successRate || 0}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Avg Execution Time
                  </Typography>
                  <Typography variant="h4">
                    {stats?.avgExecutionTime || '0s'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Executions */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Recent Executions</Typography>
              <Button variant="outlined" onClick={() => window.location.href = '/app/executions'}>
                View All
              </Button>
            </Box>
            
            {recentExecutions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No recent executions
              </Typography>
            ) : (
              <List>
                {recentExecutions.map((execution) => (
                  <ListItem key={execution.id} divider>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusIcon(execution.status)}
                          <Typography variant="body2" fontWeight="medium">
                            {execution.workflowName}
                          </Typography>
                          <Chip
                            label={execution.status}
                            color={getStatusColor(execution.status) as any}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Started: {new Date(execution.startedAt).toLocaleString()}
                          </Typography>
                          {execution.duration && (
                            <Typography variant="caption" color="text.secondary" ml={2}>
                              Duration: {execution.duration}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={() => {
                  if (!canCreateWorkflow) return;
                  window.location.href = '/app/workflows/new';
                }}
                disabled={!canCreateWorkflow}
                fullWidth
              >
                Create Workflow
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (!canViewWorkflows) return;
                  window.location.href = '/app/workflows';
                }}
                disabled={!canViewWorkflows}
                fullWidth
              >
                View Workflows
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (!canViewExecutions) return;
                  window.location.href = '/app/executions';
                }}
                disabled={!canViewExecutions}
                fullWidth
              >
                View Executions
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};